import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDocument, useFirestoreMutations } from '../../hooks/useFirestore'
import './AdminEditor.css'

const EMPTY_SCHOOL = {
  name: '',
  slug: '',
  description: '',
  icon: '',
  color: '#6E2EBF',
  unofficial: false,
  sortOrder: 0,
}

// Common emoji icons for spell schools
const SUGGESTED_ICONS = [
  { icon: '🛡️', label: 'Shield (Abjuration)' },
  { icon: '🌀', label: 'Portal (Conjuration)' },
  { icon: '👁️', label: 'Eye (Divination)' },
  { icon: '💫', label: 'Sparkles (Enchantment)' },
  { icon: '🔥', label: 'Fire (Evocation)' },
  { icon: '🎭', label: 'Mask (Illusion)' },
  { icon: '💀', label: 'Skull (Necromancy)' },
  { icon: '⚗️', label: 'Alchemy (Transmutation)' },
  { icon: '🩸', label: 'Blood (Sanguimancy)' },
  { icon: '⏳', label: 'Hourglass (Chronomancy)' },
  { icon: '🌙', label: 'Moon (Lunar)' },
  { icon: '☀️', label: 'Sun (Solar)' },
  { icon: '🌊', label: 'Wave (Hydromancy)' },
  { icon: '🌍', label: 'Earth (Geomancy)' },
  { icon: '💨', label: 'Wind (Aeromancy)' },
  { icon: '❄️', label: 'Ice (Cryomancy)' },
  { icon: '⚡', label: 'Lightning (Electromancy)' },
  { icon: '🌿', label: 'Plant (Druidic)' },
  { icon: '✨', label: 'Stars (Generic)' },
  { icon: '🔮', label: 'Crystal Ball (Mystic)' },
]

export default function SpellSchoolEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingSchool, loading } = useDocument('spellSchools', isNew ? null : id)
  const { add, update, remove } = useFirestoreMutations('spellSchools')

  const [school, setSchool] = useState(EMPTY_SCHOOL)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (existingSchool) {
      setSchool({ ...EMPTY_SCHOOL, ...existingSchool })
    }
  }, [existingSchool])

  const handleChange = (field, value) => {
    setSchool(prev => ({ ...prev, [field]: value }))

    // Auto-generate slug from name
    if (field === 'name' && (isNew || !school.slug)) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      setSchool(prev => ({ ...prev, slug }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!school.name.trim()) {
      setError('School name is required')
      return
    }

    if (!school.slug.trim()) {
      setError('Slug is required')
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (isNew) {
        await add(school)
      } else {
        await update(id, school)
      }
      navigate('/admin/spell-schools')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${school.name}"? This may break spells using this school.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/spell-schools')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Spell School' : 'Edit Spell School'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/spell-schools')}
        >
          &larr; Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <fieldset className="form-section">
          <legend>Basic Information</legend>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">School Name *</label>
              <input
                id="name"
                type="text"
                value={school.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Sanguimancy"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="slug">Slug *</label>
              <input
                id="slug"
                type="text"
                value={school.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="sanguimancy"
                required
              />
              <p className="help-text">Used internally to reference this school</p>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={school.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="The forbidden art of blood magic..."
              rows={3}
            />
          </div>
        </fieldset>

        <fieldset className="form-section">
          <legend>Appearance</legend>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="icon">Icon</label>
              <div className="icon-input-wrapper">
                <input
                  id="icon"
                  type="text"
                  value={school.icon}
                  onChange={(e) => handleChange('icon', e.target.value)}
                  placeholder="🩸"
                  className="icon-input"
                />
                <span className="icon-preview">{school.icon || '✨'}</span>
              </div>
              <p className="help-text">Emoji or single character</p>
            </div>

            <div className="form-group">
              <label htmlFor="color">Theme Color</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  value={school.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={school.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="color-text"
                  placeholder="#6E2EBF"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Suggested Icons</label>
            <div className="icon-suggestions">
              {SUGGESTED_ICONS.map(({ icon, label }) => (
                <button
                  key={icon}
                  type="button"
                  className={`icon-suggestion ${school.icon === icon ? 'selected' : ''}`}
                  onClick={() => handleChange('icon', icon)}
                  title={label}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </fieldset>

        <fieldset className="form-section">
          <legend>Classification</legend>

          <div className="form-row">
            <div className="form-group form-group-inline">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={school.unofficial === true}
                  onChange={(e) => handleChange('unofficial', e.target.checked)}
                />
                <span>Unofficial (Homebrew)</span>
              </label>
              <span className="help-text">Mark as homebrew/non-official school</span>
            </div>

            <div className="form-group">
              <label htmlFor="sortOrder">Sort Order</label>
              <input
                id="sortOrder"
                type="number"
                value={school.sortOrder}
                onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
                style={{ maxWidth: '100px' }}
              />
              <p className="help-text">Lower numbers appear first</p>
            </div>
          </div>

        </fieldset>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create School' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete School
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
